import { formatInterlocutor } from "@/helpers/CompanyHelpers";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { CardEditProps } from "@/interfaces/Card";
import CompanyCourse, { CompanyCourseForm } from "@/interfaces/CompanyCourse";
import { IFilters, IPaginateResponse, WithRequired } from "@/interfaces/Fetch";
import {
  useGetCompanyCourseQuery,
  useUpdateCompanyCourseMutation,
} from "@/services/companies/courses";

import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";
import { checkIfUserHasRoles, getReelRolesName } from "@/helpers/UserHelpers";
import useLoadOptions from "@/hooks/useLoadOptions";
import { UserFilters } from "@/interfaces/IUser";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import classNames from "classnames";
import { DateTime } from "luxon";
import {
  ElementRef,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";
import StoreCompanyCourseNoteModal from "../../modals/company/courses/StoreCompanyCourseNoteModal";
import StoreCourseModal from "../../modals/course/StoreCourseModal";

const CompanyCourseEditCard: FC<
  WithRequired<CardEditProps<CompanyCourse>, "secondId">
> = ({ id, secondId }) => {
  const user = useSelector(selectCurrentUser);
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    register,

    handleSubmit,
    reset,
  } = useForm<Partial<CompanyCourseForm>>();
  const navigation = useNavigate();

  const [updateCompanyCourseAsync] = useUpdateCompanyCourseMutation();
  const StoreCourseModalRef = useRef<ElementRef<typeof StoreCourseModal>>(null);
  const StoreCompanyCourseNoteRef =
    useRef<ElementRef<typeof StoreCompanyCourseNoteModal>>(null);

  const onTriggerNoteModal = () =>
    StoreCompanyCourseNoteRef.current?.showOrHiddenModal([id, secondId]);

  const noteButton = (
    <IconButton
      type="button"
      variant="primary"
      size="sm"
      icon={faPlus}
      iconAlign="left"
      onClick={onTriggerNoteModal}
    >
      Ajouter une note
    </IconButton>
  );

  const [reloadCourse, setReloadCourse] = useState<"0" | "1">("0");
  const [reloadInstructor, setReloadInstructor] = useState<"0" | "1">("0");

  const [instructorAdditional, setInstructorAdditional] = useState<IFilters>({
    pageIndex: 0,
  });

  const [courseAdditional, setCourseAdditional] = useState<IFilters>({
    pageIndex: 0,
  });

  const onCallback = useCallback(
    (name: string, response: IPaginateResponse<any>) => {
      if (name === "loadCompanyCourseStatusesOptions" && !watch("status")) {
        setValue("status", response.data[0]);
      }
    },
    [],
  );
  const {
    loadCoursesOptions,
    loadUsersOptions,
    loadCompanyCourseStatusesOptions,
    loadOrganisationsOptions,
  } = useLoadOptions({ onCallback });

  const defaultUserAdditional = useRef<UserFilters>({
    pageIndex: 0,
    roles: ["CSM"],
  }).current;

  const statusAdditional = useRef<IFilters>({
    pageIndex: 0,
  }).current;

  const { data } = useGetCompanyCourseQuery([id, secondId]);

  const onShowCourseModal = () =>
    StoreCourseModalRef.current?.showOrHiddenModal();

  // const onShowInstructorModal = () =>
  //   StoreInstructorModalRef.current?.showOrHiddenModal();

  useEffect(() => {
    if (data) {
      const { company, ...result } = data;
      setInstructorAdditional((old) => ({
        ...old,
        ...(company.zipCode && {
          jobAreas: [company.zipCode],
        }),
      }));
      reset(result);
    }
  }, [data]);

  const onSubmit: SubmitHandler<Partial<CompanyCourseForm>> = ({
    status,
    course,
    instructor,
    csm,
    organisation,
    ...newData
  }) => {
    const result = getDifferenceBetweenTwoObjects<
      Omit<Partial<CompanyCourseForm>, "statusId" | "courseId">
    >(data, {
      ...newData,
      instructorId: instructor?.id ?? null,
      csmId: csm?.id ?? null,
      organisationId: organisation?.id ?? null,
    });
    toast.promise(
      updateCompanyCourseAsync([
        id,
        secondId,
        {
          ...result,
          statusId: status?.id,
          courseId: course?.id,
        },
      ]).unwrap(),
      {
        pending: "Modification de la formation en cours...",
        success: {
          render() {
            navigation(-1);
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                La formation à bien été modifiée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<Partial<CompanyCourseForm>> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <div>
      {checkIfUserHasRoles(user, ["ADMIN"]) && (
        <StoreCourseModal ref={StoreCourseModalRef} />
      )}
      <StoreCompanyCourseNoteModal ref={StoreCompanyCourseNoteRef} />
      <Form onSubmit={handleSubmit(onSubmit, onError)}>
        <Card>
          <CardHeader title="Modification de la formation" endEl={noteButton} />
          <Card.Body className="bg-light">
            <Controller
              control={control}
              name="status"
              render={({ field: status }) => (
                <Row className="gx-3">
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Statut <span className="text-danger">*</span> :
                      </Form.Label>
                      <SelectPaginateAsync
                        loadOptions={loadCompanyCourseStatusesOptions}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id.toString()}
                        isClearable
                        value={status.value}
                        onChange={status.onChange}
                        additional={statusAdditional}
                      />
                      <Form.Text className="text-muted">
                        En fonction du statut séléctionné, il y aura plus ou
                        moins de champs obligatoires.
                      </Form.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.status?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>CSM :</Form.Label>
                      <Controller
                        control={control}
                        name="csm"
                        render={({ field }) => (
                          <>
                            <SelectPaginateAsync
                              loadOptions={loadUsersOptions}
                              getOptionLabel={(option) =>
                                `${option.fullname} - ${getReelRolesName(
                                  option.roles,
                                )}`
                              }
                              getOptionValue={(option) => option.id.toString()}
                              additional={defaultUserAdditional}
                              value={field.value}
                              onChange={(value) => field.onChange(value)}
                            />
                            {Boolean(errors.csm) ? (
                              <h6 className="mb-0 mt-0 mt-1 text-danger">
                                {errors.csm?.message}
                              </h6>
                            ) : null}
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Flex className="flex-between-center">
                        <Form.Label>
                          Formation <span className="text-danger">*</span>
                        </Form.Label>
                        {checkIfUserHasRoles(user, ["ADMIN"]) && (
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0"
                            onClick={onShowCourseModal}
                          >
                            Ajouter
                          </Button>
                        )}
                      </Flex>
                      <Controller
                        control={control}
                        name="course"
                        rules={{
                          required: "Veuillez sélectionner une formation",
                        }}
                        render={({ field }) => (
                          <SelectPaginateAsync
                            key={reloadCourse}
                            loadOptions={loadCoursesOptions}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id.toString()}
                            value={field.value}
                            isClearable
                            onChange={field.onChange}
                            additional={courseAdditional}
                          />
                        )}
                      />
                      <Form.Text className="text-muted">
                        Vous devez choisir une formation qui correspond aux
                        catégories du formateur
                      </Form.Text>
                      <Form.Control.Feedback
                        type="invalid"
                        className={classNames({
                          "d-block": errors.course,
                        })}
                      >
                        {errors.course?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Organisme de formation</Form.Label>
                      <Controller
                        control={control}
                        name="organisation"
                        render={({ field }) => (
                          <SelectPaginateAsync
                            loadOptions={loadOrganisationsOptions}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id.toString()}
                            value={field.value}
                            isClearable
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Type de formation</Form.Label>
                      <Form.Select
                        {...register("trainingType")}
                        isInvalid={!!errors.trainingType}
                      >
                        <option value="">Sélectionner un type</option>
                        <option value="presentiel">Présentiel</option>
                        <option value="distanciel">Distanciel</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.trainingType?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de signature</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("signatureDate")}
                        isInvalid={!!errors.signatureDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.signatureDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Numéro de dossier définitif</Form.Label>
                      <Form.Control
                        {...register("finalFolderNumber", {
                          onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase();
                          },
                        })}
                        isInvalid={!!errors.finalFolderNumber}
                        placeholder="ex : 2023-ABC123"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.finalFolderNumber?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Interlocuteur</Form.Label>
                      {/* Celui de l'entreprise : une formation n'a pas d'interlocuteur à elle. */}
                      <Form.Control
                        readOnly
                        disabled
                        value={formatInterlocutor(
                        data?.company?.interlocutorFirstname,
                        data?.company?.interlocutorLastname
                      ) ?? ""}
                        placeholder="Aucun interlocuteur sur l'entreprise"
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de relance</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("followUpDate", {
                          validate: (value) => {
                            if (value) {
                              if (
                                DateTime.fromISO(value) <
                                DateTime.now().startOf("day")
                              ) {
                                return "La date de relance ne peut pas être dans le passé";
                              }
                            }
                          },
                        })}
                        isInvalid={!!errors.followUpDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.followUpDate?.message}
                      </Form.Control.Feedback>
                      <Form.Text>
                        La date de relance doit être aujourd'hui ou plus tard
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de début de formation</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("startAt")}
                        isInvalid={!!errors.startAt}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.startAt?.message}
                      </Form.Control.Feedback>
                      <Form.Text>
                        La date de début de formation doit être supérieur à la
                        date d'aujourd'hui
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de fin de formation</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("endAt")}
                        isInvalid={!!errors.endAt}
                      />
                      <Form.Text>
                        La date de fin de formation doit être supérieur à la
                        date d'aujourd'hui
                      </Form.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.endAt?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nombre d'heures</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          placeholder="ex : 3"
                          {...register("hoursNumber")}
                          isInvalid={!!errors.hoursNumber}
                        />
                        <InputGroup.Text>heure(s)</InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.hoursNumber?.message}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Montant demandé</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          placeholder="ex : 5000"
                          {...register("askingPrice")}
                          isInvalid={!!errors.askingPrice}
                        />
                        <InputGroup.Text>€</InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.askingPrice?.message}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date d'envoi</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("sendingDate")}
                        isInvalid={!!errors.sendingDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.sendingDate?.message}
                      </Form.Control.Feedback>
                      <Form.Text>
                        La date d'envoi doit être aujourd'hui ou plus tard
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de début de session</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("sessionStartDate")}
                        isInvalid={!!errors.sessionStartDate}
                      />
                      <Form.Text>
                        La date de début de session doit être aujourd'hui ou
                        plus tard
                      </Form.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.sessionStartDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de fin de session</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("sessionEndDate")}
                        isInvalid={!!errors.sessionEndDate}
                      />
                      <Form.Text>
                        La date de fin de session doit être aujourd'hui ou plus
                        tard
                      </Form.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.sessionEndDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date d'accord</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("agreementDate")}
                        isInvalid={!!errors.agreementDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.agreementDate?.message}
                      </Form.Control.Feedback>
                      <Form.Text>
                        La date d'accord doit être aujourd'hui ou plus tard
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Montant accordé</Form.Label>
                      <InputGroup className="mb-2">
                        <Form.Control
                          type="number"
                          placeholder="ex : 5000"
                          {...register("agreementAmount")}
                          isInvalid={!!errors.agreementAmount}
                        />
                        <InputGroup.Text>€</InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.agreementAmount?.message}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Numéro de facture</Form.Label>
                      <Form.Control
                        {...register("invoiceNumber", {
                          onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase();
                          },
                        })}
                        isInvalid={!!errors.invoiceNumber}
                        placeholder="ex : FA2023-5678"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.invoiceNumber?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de facture</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("invoiceDate")}
                        isInvalid={!!errors.invoiceDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.invoiceDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Montant de la facture</Form.Label>
                      <InputGroup className="mb-2">
                        <Form.Control
                          type="number"
                          placeholder="ex : 670.99"
                          {...register("invoiceAmount")}
                          isInvalid={!!errors.invoiceAmount}
                        />
                        <InputGroup.Text>€</InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.invoiceAmount?.message}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label>Date de paiement</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("paiementDate")}
                        isInvalid={!!errors.paiementDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.paiementDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {/* <Col xs={12} className="mt-3">
                    <hr />
                    <h4 className="my-4">Informations sur le formateur :</h4>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Flex className="flex-between-center">
                        <Form.Label>Formateur</Form.Label>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0"
                          // onClick={onShowInstructorModal}
                        >
                          Ajouter
                        </Button>
                      </Flex>
                      <Controller
                        control={control}
                        name="instructor"
                        render={({ field }) => (
                          <SelectPaginateAsync
                            key={reloadInstructor}
                            loadOptions={loadInstructorsOptions}
                            value={field.value}
                            getOptionLabel={(option) => option.fullname}
                            getOptionValue={(option) => option.id.toString()}
                            additional={instructorAdditional}
                            isClearable
                            // onChange={onInstructorChange}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.instructor?.message}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Vous devez choisir un formateur qui correspond aux
                        catégories de la formation
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Numéro de facture du formateur</Form.Label>
                      <Form.Control
                        type="text"
                        {...register("instructorInvoiceNumber")}
                        placeholder="ex : FA2023-5678"
                        isInvalid={!!errors.instructorInvoiceNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.instructorInvoiceNumber?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de facture du formateur</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("instructorInvoiceDate")}
                        isInvalid={!!errors.instructorInvoiceDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.instructorInvoiceDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Date de paiement de la facture du formateur
                      </Form.Label>
                      <Form.Control
                        type="date"
                        {...register("instructorInvoicePaiementDate")}
                        isInvalid={!!errors.instructorInvoicePaiementDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.instructorInvoicePaiementDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col> */}

                  <Col xs={12} className="mt-3">
                    <hr />
                    <h4 className="my-4">Autres informations :</h4>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date d'administration</Form.Label>
                      <Form.Control
                        type="date"
                        {...register("adminDate")}
                        isInvalid={!!errors.adminDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.adminDate?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            />
          </Card.Body>
          <Card.Footer>
            <Flex justifyContent="between" alignItems="center">
              <Button type="submit" variant="success">
                Confirmer
              </Button>
              {noteButton}
            </Flex>
          </Card.Footer>
        </Card>
      </Form>
    </div>
  );
};

export default CompanyCourseEditCard;
